import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

const TreatmentConsentLandingPage = ({
  title,
  description,
  canonicalUrl,
  h1,
  intro,
  treatmentName,
  risks,
  relatedLinks = [],
  /** Shown as a clear callout above “Related” — use for NHS/NICE links patients may share */
  officialGuidanceLinks = [],
}) => {
  usePageMeta(title, description, canonicalUrl);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { label: 'Dental consent forms', path: '/dental-consent-forms' },
            { label: treatmentName, path: canonicalUrl.replace('https://smileconsent.co.uk', '') },
          ]}
        />
      </div>

      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-800 mb-6">
            <SparklesIcon className="h-4 w-4 mr-2" />
            AI-assisted dental consent for UK practices
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {h1}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-8">
            {intro}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center justify-center gap-2"
            >
              Start Free Trial
              <ArrowRightIcon className="h-5 w-5" />
            </Link>
            <Link
              to="/book-demo"
              className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Demo
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="rounded-xl bg-blue-100 p-3">
                  <DocumentTextIcon className="h-7 w-7 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  What a strong {treatmentName.toLowerCase()} should cover
                </h2>
              </div>
              <ul className="space-y-4">
                {risks.map((risk) => (
                  <li key={risk} className="flex items-start gap-3 text-gray-700">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-gray-200 p-8 shadow-sm bg-gray-50">
              <div className="flex items-center gap-3 mb-5">
                <div className="rounded-xl bg-green-100 p-3">
                  <ShieldCheckIcon className="h-7 w-7 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Why practices use Smile Consent
                </h2>
              </div>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <span>Generate treatment-specific wording faster with AI-assisted drafting.</span>
                </li>
                <li className="flex items-start gap-3">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <span>Include costs, risks, alternatives, and expected outcomes in a clearer format.</span>
                </li>
                <li className="flex items-start gap-3">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <span>Send for digital signature and keep a timestamped audit trail for the patient record.</span>
                </li>
                <li className="flex items-start gap-3">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <span>Support a better consent process for UK practices working to GDC and CQC expectations.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {officialGuidanceLinks.length > 0 && (
        <section className="py-12 bg-slate-50 border-y border-slate-200" aria-labelledby="official-guidance-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="official-guidance-heading" className="text-xl font-bold text-gray-900 mb-2">
              Official guidance
            </h2>
            <p className="text-gray-600 mb-5 text-sm sm:text-base">
              Independent references you can open or share with patients and colleagues.
            </p>
            <ul className="space-y-3">
              {officialGuidanceLinks.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <span>
                      <span className="font-semibold text-gray-900 group-hover:text-blue-700">{item.title}</span>
                      {item.description ? (
                        <span className="block text-sm text-gray-600 mt-1">{item.description}</span>
                      ) : null}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 shrink-0">
                      {item.ctaLabel ?? 'View guidance'}
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 sm:p-10">
            <h2 className="text-2xl font-bold mb-4">
              Build {treatmentName.toLowerCase()} faster
            </h2>
            <p className="text-blue-100 text-lg leading-relaxed mb-6">
              Smile Consent helps UK dental teams create clearer, treatment-specific consent records without relying on paper forms or generic wording.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
              >
                Start free trial
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <Link
                to="/dental-consent-forms"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors"
              >
                View all consent forms
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InternalLinks title="Related Treatment Consent Pages" links={relatedLinks} />
        </div>
      </section>
    </div>
  );
};

export default TreatmentConsentLandingPage;
